import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { DollarSign, Receipt, TrendingUp, Store, RefreshCw, AlertTriangle, CheckCircle2, FileText, FileSpreadsheet, Download, Percent, Users } from 'lucide-react';
import { ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import { toast } from 'sonner';
import { API, isMgmt } from '../lib/ops';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';

const cv = {
  get: (p, params) => axios.get(`${API}/clearview${p}`, { params }).then(r => r.data),
};
const money = (v) => v == null ? '—' : `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const num = (v) => v == null ? '—' : Number(v).toLocaleString();

function daysAgo(n) { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); }

export default function SalesReports() {
  const { user } = useAuth();
  const mgmt = isMgmt(user);
  const [start, setStart] = useState(daysAgo(14));
  const [end, setEnd] = useState(daysAgo(1));
  const [mode, setMode] = useState('consolidated');
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    const params = mode === 'consolidated' ? { start, end, consolidated: true } : { start, end, consolidated: false };
    cv.get('/data', params).then(r => setRows(r.rows)).catch(e => toast.error(e.response?.data?.detail || 'Failed to load')).finally(() => setLoading(false));
    cv.get('/summary', { start, end, mode: mgmt ? 'consolidated' : 'stores' }).then(setSummary).catch(() => setSummary(null));
  }, [start, end, mode, mgmt]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (mgmt) cv.get('/status').then(setStatus).catch(() => {}); }, [mgmt]);

  const download = async (fmt) => {
    try {
      const res = await axios.get(`${API}/clearview/export/${fmt}`, { params: { start, end, mode: mode === 'stores' ? 'stores' : 'consolidated' }, responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = `Elmwood_Sales_${start}_to_${end}.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error('Export failed'); }
  };

  const totSales = rows.reduce((a, r) => a + (r.net_sales || 0), 0);
  const totTxns = rows.reduce((a, r) => a + (r.transactions || 0), 0);
  const avg = totTxns ? totSales / totTxns : null;
  // per-store aggregation for comparison table
  const byStore = {};
  rows.forEach(r => {
    const k = r.store_label;
    byStore[k] = byStore[k] || { label: k, sales: 0, txns: 0, days: 0 };
    byStore[k].sales += r.net_sales || 0; byStore[k].txns += r.transactions || 0; byStore[k].days += 1;
  });
  const storeRows = Object.values(byStore).sort((a, b) => b.sales - a.sales);
  const best = storeRows[0], worst = storeRows[storeRows.length - 1];

  // per-date series for trend charts
  const byDate = {};
  rows.forEach(r => {
    const d = byDate[r.date] = byDate[r.date] || { date: r.date.slice(5), sales: 0, txns: 0, lw: 0, ls: 0 };
    d.sales += r.net_sales || 0; d.txns += r.transactions || 0;
    if (r.labour_pct != null && r.net_sales) { d.lw += r.labour_pct * r.net_sales; d.ls += r.net_sales; }
  });
  const series = Object.values(byDate).sort((a, b) => a.date.localeCompare(b.date)).map(d => ({ date: d.date, sales: Math.round(d.sales), txns: d.txns, labour: d.ls ? +(d.lw / d.ls).toFixed(1) : null }));
  const cur = summary?.current || { net_sales: totSales, transactions: totTxns, avg_ticket: avg, labour_pct: null, food_pct: null };
  const vsLy = summary?.sales_vs_ly_pct;
  const cards = [
    { l: 'Net Sales', v: money(cur.net_sales), Icon: DollarSign, c: 'text-emerald-600', sub: vsLy != null ? `${vsLy >= 0 ? '+' : ''}${vsLy}% vs last yr` : 'No LY data yet', subGood: vsLy >= 0 },
    { l: 'Transactions', v: num(cur.transactions), Icon: Receipt, c: 'text-blue-600' },
    { l: 'Avg Ticket', v: cur.avg_ticket == null ? '—' : `$${cur.avg_ticket.toFixed(2)}`, Icon: TrendingUp, c: 'text-[#F26522]' },
    { l: 'Labour %', v: cur.labour_pct == null ? '—' : `${cur.labour_pct.toFixed(1)}%`, Icon: Users, c: 'text-violet-600' },
    { l: 'Food %', v: cur.food_pct == null ? '—' : `${cur.food_pct.toFixed(1)}%`, Icon: Percent, c: 'text-amber-600' },
  ];

  return (
    <div className="space-y-5 max-w-5xl" data-testid="sales-reports-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <p className="text-[11px] uppercase tracking-[0.25em] text-[#F26522] font-semibold">Sales · Clearview</p>
          <h1 className="text-2xl sm:text-3xl font-black text-[#3E2418] tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>Sales &amp; Reports</h1>
          {status && <p className="text-xs text-slate-500 mt-1" data-testid="cv-last-synced">Last synced: {status.last_synced_at ? new Date(status.last_synced_at).toLocaleString() : 'never'}{status.latest ? ` · data through ${status.latest}` : ''}</p>}
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => download('pdf')} variant="outline" size="sm" className="rounded-full" data-testid="cv-export-pdf"><FileText className="w-4 h-4 mr-1" />PDF</Button>
          <Button onClick={() => download('xlsx')} variant="outline" size="sm" className="rounded-full" data-testid="cv-export-xlsx"><FileSpreadsheet className="w-4 h-4 mr-1" />Excel</Button>
          <Button onClick={() => download('csv')} variant="outline" size="sm" className="rounded-full" data-testid="cv-export-csv"><Download className="w-4 h-4 mr-1" />CSV</Button>
          <Button onClick={load} variant="outline" size="sm" className="rounded-full" data-testid="cv-refresh"><RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />Refresh</Button>
        </div>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3 bg-white border border-[#E5E1DA] rounded-2xl p-3">
        <label className="flex items-center gap-1.5 text-xs text-slate-500">From<input type="date" value={start} onChange={e => setStart(e.target.value)} className="rounded-md border border-slate-200 px-2 py-1 text-xs" data-testid="cv-start" /></label>
        <label className="flex items-center gap-1.5 text-xs text-slate-500">To<input type="date" value={end} onChange={e => setEnd(e.target.value)} className="rounded-md border border-slate-200 px-2 py-1 text-xs" data-testid="cv-end" /></label>
        {mgmt && (
          <div className="flex bg-slate-100 rounded-full p-0.5 ml-auto" data-testid="cv-mode">
            <button onClick={() => setMode('consolidated')} className={`text-xs font-semibold px-3 py-1.5 rounded-full ${mode === 'consolidated' ? 'bg-white text-[#3E2418] shadow-sm' : 'text-slate-500'}`} data-testid="cv-mode-consolidated">Consolidated</button>
            <button onClick={() => setMode('stores')} className={`text-xs font-semibold px-3 py-1.5 rounded-full ${mode === 'stores' ? 'bg-white text-[#3E2418] shadow-sm' : 'text-slate-500'}`} data-testid="cv-mode-stores">By store</button>
          </div>
        )}
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="cv-kpis">
        {cards.map(({ l, v, Icon, c, sub, subGood }) => (
          <div key={l} className="bg-white border border-[#E5E1DA] rounded-2xl p-4" data-testid={`cv-kpi-${l.toLowerCase().replace(/ |%/g, '-').replace(/-+$/, '')}`}>
            <Icon className={`w-4 h-4 ${c}`} />
            <p className={`text-2xl font-black mt-1.5 ${c}`} style={{ fontFamily: 'Chivo, sans-serif' }}>{v}</p>
            <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{l}</p>
            {sub && <p className={`text-[11px] font-semibold mt-0.5 ${subGood ? 'text-emerald-600' : vsLy != null ? 'text-rose-600' : 'text-slate-400'}`}>{sub}</p>}
          </div>
        ))}
      </div>

      {/* Trend charts */}
      {series.length > 0 && (
        <div className="grid lg:grid-cols-2 gap-3" data-testid="cv-charts">
          <div className="bg-white border border-[#E5E1DA] rounded-2xl p-4">
            <h3 className="font-bold text-[#3E2418] text-sm mb-3">Net sales trend</h3>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={series} margin={{ left: -10, right: 8, top: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(v) => `$${Number(v).toLocaleString()}`} />
                <Line type="monotone" dataKey="sales" stroke="#F26522" strokeWidth={2.5} dot={false} name="Net Sales" />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="bg-white border border-[#E5E1DA] rounded-2xl p-4">
            <h3 className="font-bold text-[#3E2418] text-sm mb-3">Transactions &amp; labour %</h3>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={series} margin={{ left: -10, right: 8, top: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                <YAxis yAxisId="l" tick={{ fontSize: 10 }} />
                <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 10 }} tickFormatter={v => `${v}%`} />
                <Tooltip />
                <Line yAxisId="l" type="monotone" dataKey="txns" stroke="#2563eb" strokeWidth={2} dot={false} name="Transactions" />
                <Line yAxisId="r" type="monotone" dataKey="labour" stroke="#7c3aed" strokeWidth={2} dot={false} name="Labour %" connectNulls />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {status && status.not_reporting?.length > 0 && (
        <div className="flex items-center gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2" data-testid="cv-not-reporting">
          <AlertTriangle className="w-4 h-4" /> Not reporting on last sync: {status.not_reporting.join(', ')}
        </div>
      )}

      {/* Store comparison (management, by-store mode) */}
      {mgmt && mode === 'stores' && storeRows.length > 0 && (
        <div className="bg-white border border-[#E5E1DA] rounded-2xl p-4 sm:p-5" data-testid="cv-store-chart">
          <h3 className="font-bold text-[#3E2418] text-sm mb-3">Store comparison — net sales</h3>
          <ResponsiveContainer width="100%" height={Math.max(180, storeRows.length * 30)}>
            <BarChart data={storeRows.map(s => ({ name: s.label.replace(/ ?\/.*/, ''), sales: Math.round(s.sales) }))} layout="vertical" margin={{ left: 20, right: 12 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={90} />
              <Tooltip formatter={(v) => `$${Number(v).toLocaleString()}`} />
              <Bar dataKey="sales" fill="#F26522" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Store comparison table (management, by-store mode) */}
      {mgmt && mode === 'stores' && storeRows.length > 0 && (
        <div className="bg-white border border-[#E5E1DA] rounded-2xl p-4 sm:p-5" data-testid="cv-store-table">
          <h3 className="font-bold text-[#3E2418] text-sm mb-3">Store comparison · {start} → {end}</h3>
          <div className="space-y-1">
            {storeRows.map((s, i) => (
              <div key={s.label} className="flex items-center gap-3 py-2 border-b border-slate-50 last:border-0" data-testid={`cv-store-row-${i}`}>
                <span className="text-xs text-slate-400 w-5">{i + 1}</span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-slate-800 truncate">{s.label}
                    {best && s.label === best.label && <span className="ml-2 text-[10px] font-bold text-emerald-700 bg-emerald-100 rounded-full px-2 py-0.5">TOP</span>}
                    {worst && storeRows.length > 1 && s.label === worst.label && <span className="ml-2 text-[10px] font-bold text-rose-700 bg-rose-100 rounded-full px-2 py-0.5">LOWEST</span>}
                  </p>
                  <div className="h-1.5 mt-1 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-[#F26522]" style={{ width: `${best && best.sales ? (s.sales / best.sales) * 100 : 0}%` }} /></div>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-sm font-bold text-[#3E2418]">{money(s.sales)}</p>
                  <p className="text-[11px] text-slate-400">{num(s.txns)} txns · ${s.txns ? (s.sales / s.txns).toFixed(2) : '0'}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Daily list (consolidated or store-level) */}
      {mode !== 'stores' && (
        <div className="bg-white border border-[#E5E1DA] rounded-2xl p-4 sm:p-5" data-testid="cv-daily-table">
          <h3 className="font-bold text-[#3E2418] text-sm mb-3">Daily sales</h3>
          {rows.length === 0 ? <p className="text-sm text-slate-400 py-4">No synced data for this range yet.</p> : (
            <div className="space-y-1">
              {rows.slice().reverse().map(r => (
                <div key={r.date} className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0">
                  <span className="text-sm text-slate-600">{r.date}{!r.reporting && <span className="ml-2 text-[10px] font-bold text-rose-600">NOT REPORTING</span>}</span>
                  <div className="flex items-center gap-4"><span className="text-sm font-bold text-[#3E2418]">{money(r.net_sales)}</span><span className="text-xs text-slate-400 w-24 text-right">{num(r.transactions)} txns</span></div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
